/* eslint-disable no-restricted-syntax */
import type { BreederListPageLitterCategory } from '@presentation/pages';
import { groupBy } from 'lodash/fp';
import { useMemo } from 'react';
import type { LocationDescriptor } from 'history';
import { useRouter } from '@application/utils';
import { useGetLitters } from './api/useGetLitters';
import { useFormatLitter } from './api/useFormatLitter';

/**
 * Helper to get the litters of the selected breeds, and categorise them by breed.
 * Subbreeds like:
 * Chihuahua -> Chihuahua poil court / Chihuahua poil long
 * should be grouped by the base breed name translation, so
 * all of the above will be under the category "Chihuahua"
 *  */

export interface UseLittersCategoriesParams {
  show: boolean;
}

export const useLittersCategories = (params: UseLittersCategoriesParams) => {
  const { show } = params;
  const { routeLocationDescriptor } = useRouter();
  const { formatLitter } = useFormatLitter();
  const { litters, littersLoading, count, littersRefetch } = useGetLitters({
    forceSkip: !show,
  });

  const litterCategories = useMemo((): BreederListPageLitterCategory[] => {
    if (!show || !litters?.length) return [];
    const breedTranslationNameByBreedName: any = {};

    for (const litter of litters) {
      const key = litter.dogBreed?.name;
      const value = litter.dogBreed?.translations[0].name;
      if (key && value) {
        breedTranslationNameByBreedName[key] = value;
      }
    }
    const formattedLittersWithBaseBreed = litters.map((litter) => {
      const baseBreedName = litter.dogBreed?.baseBreedName;
      const breedName = litter.dogBreed?.name;
      const breedCategoryName =
        breedTranslationNameByBreedName[baseBreedName as any] ??
        breedTranslationNameByBreedName[breedName as any];
      return {
        ...formatLitter(litter),
        breedCategoryName,
        breedName,
        baseBreedName,
      };
    });

    const map = groupBy((litter) => {
      return litter.breedCategoryName;
    }, formattedLittersWithBaseBreed);

    return Object.keys(map ?? {}).map((key) => {
      const link: LocationDescriptor = routeLocationDescriptor('litterList', {
        params: [String(map[key]?.[0]?.baseBreedName ?? map[key]?.[0]?.breedName)],
      });
      const canLoadMoreRelatedLitters = map[key].length > 4 && count > 4;
      return {
        label: key,
        litters: map[key].splice(0, 4),
        canLoadMoreRelatedLitters,
        link,
      };
    });
  }, [show, litters, formatLitter, routeLocationDescriptor, count]);

  return { litterCategories, littersLoading, littersRefetch };
};
